import React, { Component } from 'react';
import AvatarImage from '../../../../elements/AvatarImage';
import Dropzone from 'react-dropzone';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button, Icon, Input, Message, Select } from 'semantic-ui-react';
import config from '../../../../config';
import noavatar from '../../../../public/images/noavatar.png';
import actions from '../../../../redux/actions';
import { OperationStatus } from '../../../../types/OperationStatus';
import { UserStatus } from '../../../../types/UserStatus';
import countryCodes from '../../../../util/options/countryCodes';
import { searchFromStart } from '../../../../util/utils';

const mapStateToProps = (state) => {
  return {
    userUploadAvatarStatus: state.userUploadAvatarStatus,
    userSaveStatus: state.userSaveStatus,
    passwordChangeStatus: state.passwordChangeStatus,
    user: state.user,
    devices: state.devices,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: (data) => dispatch(actions.updateUser(data)),
    saveUser: (data) => dispatch(actions.saveUser(data)),
    uploadAvatar: (avatarFile) => dispatch(actions.uploadAvatar(avatarFile)),
  };
};

const mandatoryFields = ['companyName', 'firstName', 'lastName', 'companyAddress', 'companyHqAddress', 'companyUrl', 'position'];

class Personal extends Component<any> {
  public state: any;

  constructor(props) {
    super(props);

    this.state = {
      newAvatar: {},
      newAvatarError: '',
      errors: [],
    };
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.user.status !== prevProps.user.status &&
      this.props.userSaveStatus.status === OperationStatus.success
    ) {
      this.props.updateUser({ status: UserStatus.Active });
      this.props.history.push('/account');
    }
  }

  updateErrorState = (event, data) => {
    if (this.state.errors.includes(data.name)) {
      const errors = [...this.state.errors];
      errors.splice(this.state.errors.indexOf(data.name), 1);
      this.setState({ errors });
    }
  };

  onChange = (event, data) => {
    const user = { ...this.props.user };
    user[data.name] = data.value;

    return this.props.updateUser(user);
  };

  isSaveEnabled = () => {
    for (const el of mandatoryFields) {
      if (!this.props.user[el]) {
        return false;
      }
    }

    return true;
  };

  onAvatarUpload = ([acceptedImageFile]) => {
    const newAvatar = Object.assign(acceptedImageFile, {
      preview: URL.createObjectURL(acceptedImageFile),
    });

    this.setState({
      newAvatar,
      newAvatarError: '',
    });
  };

  onAvatarRejected = () => {
    this.setState({ newAvatarError: 'Avatar size can be no more than 1 Mb' });
  };

  onSave = async () => {
    const user = { ...this.props.user };
    const errors: any[] = [];

    mandatoryFields.forEach((el) => {
      if (!user[el]) {
        errors.push(el);
      }
    });

    if (errors.length) {
      return this.setState({ errors });
    }

    delete user.avatarUrl;

    if (Object.keys(this.state.newAvatar).length) {
      await this.props.uploadAvatar(this.state.newAvatar);
    }

    this.props.saveUser(user);
  };

  render() {
    const userSaveProcessing = this.props.userSaveStatus.status === OperationStatus.processing || this.props.userUploadAvatarStatus.status === OperationStatus.processing;
    const userSaveEnabled = this.isSaveEnabled();

    let avatarUrl;

    if (this.state.newAvatar.preview) {
      avatarUrl = this.state.newAvatar.preview;
    } else if (this.props.user.avatarUrl) {
      const href = new URL(this.props.user.avatarUrl, config.apiEndpoint).href;
      avatarUrl = href;
    } else {
      avatarUrl = noavatar;
    }

    return (
      <div className="step-personal">
        <Message info icon>
          <Icon name="address card outline" />
          <Message.Content>
            <Message.Header>Welcome!</Message.Header>
            <p>Please fill the information below to complete your registration</p>
          </Message.Content>
        </Message>
        <table className="personal-information">
          <tbody>
            <tr>
              <td>Photo</td>
              <td className="avatar-cell">
                <AvatarImage className="avatar" src={avatarUrl} alt="avatar" height="128" width="128" />
                {this.state.newAvatarError && (
                  <Message negative compact size="mini">
                    {this.state.newAvatarError}
                  </Message>
                )}
                <Dropzone
                  multiple={false}
                  maxSize={1000000}
                  accept="image/jpeg, image/png"
                  onDropAccepted={this.onAvatarUpload}
                  onDropRejected={this.onAvatarRejected}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Button primary size="tiny" style={{ width: '128px' }} content="Change" />
                    </div>
                  )}
                </Dropzone>
              </td>
            </tr>
            <tr>
              <td>First name</td>
              <td>
                <Input
                  placeholder="First name"
                  name="firstName"
                  value={this.props.user.firstName}
                  onChange={this.onChange}
                  onOpen={this.updateErrorState}
                  error={this.state.errors.includes('firstName')}
                />
              </td>
            </tr>
            <tr>
              <td>Last name</td>
              <td>
                <Input
                  placeholder="Last name"
                  name="lastName"
                  value={this.props.user.lastName}
                  onChange={this.onChange}
                  onOpen={this.updateErrorState}
                  error={this.state.errors.includes('lastName')}
                />
              </td>
            </tr>
            <tr>
              <td>Country</td>
              <td>
                <Select
                  name="country"
                  placeholder="Country"
                  options={countryCodes}
                  value={this.props.user.country}
                  onChange={this.onChange}
                  onOpen={this.updateErrorState}
                  error={this.state.errors.includes('country')}
                  search={searchFromStart}
                />
              </td>
            </tr>
            <tr>
              <td>Company name</td>
              <td>
                <Input
                  placeholder="Company name"
                  name="companyName"
                  value={this.props.user.companyName}
                  onChange={this.onChange}
                  onOpen={this.updateErrorState}
                  error={this.state.errors.includes('companyName')}
                />
              </td>
            </tr>
            <tr>
              <td>Company address</td>
              <td>
                <Input
                  placeholder="Company address"
                  name="companyAddress"
                  value={this.props.user.companyAddress}
                  onChange={this.onChange}
                />
              </td>
            </tr>
            <tr>
              <td>Company HQ address</td>
              <td>
                <Input
                  placeholder="Company HQ address"
                  name="companyHqAddress"
                  value={this.props.user.companyHqAddress}
                  onChange={this.onChange}
                />
              </td>
            </tr>
            <tr>
              <td>Position</td>
              <td>
                <Input
                  placeholder="Position"
                  name="position"
                  value={this.props.user.position}
                  onChange={this.onChange}
                />
              </td>
            </tr>
            <tr>
              <td>Company URL</td>
              <td>
                <Input
                  placeholder="Company URL"
                  name="companyUrl"
                  value={this.props.user.companyUrl}
                  onChange={this.onChange}
                />
              </td>
            </tr>
          </tbody>
        </table>

        <Button
          className="save-and-next"
          primary
          icon
          size="big"
          labelPosition="right"
          onClick={this.onSave}
          loading={userSaveProcessing}
          disabled={!userSaveEnabled}
        >
          Save &amp; next
          <Icon name="arrow right" />
        </Button>
      </div>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Personal));
